/**
 * Açı Kontrolü - working through the photographs nobody has placed yet.
 *
 * ## Why a queue and not a list
 *
 * The media card already has a dropdown per photograph, and that is the right
 * control for correcting one file. It is the wrong one for a hundred and
 * fifty, which is the real situation: a session's photographs arrive unmarked,
 * the estimator narrows most of them to two candidates, and somebody has to
 * work through them. A grid of a hundred and fifty dropdowns is a screen
 * nobody finishes.
 *
 * So this shows one photograph at a time, large, with the candidates as
 * buttons. Answering advances. That is the difference between reviewing and
 * data entry.
 *
 * ## The model proposes; it never decides
 *
 * No subject's benchmark has cleared the 95% release gate, so nothing here is
 * pre-filled and no answer is written until somebody presses it. The
 * shortlist - usually two of twelve for a head, two of six for an object - is
 * the whole of what a model below the gate contributes, and the screen says
 * so rather than leaving somebody to wonder why a suggestion is not applied.
 *
 * The full list of stations is always available beside the shortlist. A
 * two-button screen that hid the other ten would make the model's narrowing
 * binding, which is exactly what the gate exists to prevent.
 *
 * ## "None of these" is an answer
 *
 * A photograph of the floor while somebody was walking is not one of the
 * subject's stations, and marking it so is a real decision that stops the
 * estimator proposing something for it forever after. It is not a skip.
 *
 * Skipping is different and is deliberately not stored: "I do not want to
 * decide this now" is not a decision, and a column recording it would let a
 * skipped photograph leave the queue while staying unmarked forever. The
 * client advances; the file is there next time.
 */

import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";

import { assignReviewedRegion, getRegionReview } from "@/api/client";
import { apiUrl } from "@/api/config";
import type { RegionReviewItem, SubjectType } from "@/api/types";
import { ErrorState } from "@/components/ErrorState";
import { PageHeader, Section } from "@/components/product";
import { Button, Callout, Loading } from "@/components/ui";
import { guidedRegionLabel } from "@/features/coverage/guidedCoverage";
import { useAsync } from "@/lib/useAsync";
import "./coverage.css";

/** How many the queue holds at once. Answering the last one fetches more. */
const PAGE = 25;

const label = (subject: SubjectType, region: string): string =>
  guidedRegionLabel(subject, region) ?? region;

export function RegionReviewScreen() {
  const { projectId = "" } = useParams();
  const fetcher = useCallback(
    (signal: AbortSignal) => getRegionReview(projectId, { limit: PAGE }, { signal }),
    [projectId],
  );
  const { state, reload } = useAsync(fetcher);

  /**
   * How far into the loaded page the reader has got.
   *
   * Local rather than derived from the queue, because skipping has to move
   * forward without changing anything on the server. Reset whenever a page is
   * re-read.
   */
  const [cursor, setCursor] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [done, setDone] = useState(0);

  // `state.data` survives a reload, so answering the last photograph of a page
  // fetches the next one without blanking the screen. Only a first load has
  // nothing to draw.
  if (state.status === "error") {
    return <ErrorState error={state.error} onRetry={reload} />;
  }
  const review = state.data;
  if (review === null) return <Loading label="Kontrol kuyruğu yükleniyor" />;

  const subject = review.subject_type;
  const item: RegionReviewItem | undefined = review.items[cursor];

  const advance = () => {
    // Past the end of the loaded page: fetch the next one rather than telling
    // somebody they are finished when the project has three hundred more.
    if (cursor + 1 >= review.items.length && review.remaining > review.items.length) {
      setCursor(0);
      reload();
      return;
    }
    setCursor((current) => current + 1);
  };

  const answer = async (region: string | null) => {
    if (!item || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await assignReviewedRegion(item.media.id, region);
      setDone((current) => current + 1);
      advance();
    } catch {
      // Left on the same photograph. Advancing after a failed write would
      // silently skip a file the reader believes they answered.
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const remaining = Math.max(review.remaining - done, 0);
  const rest = item
    ? review.regions.filter((region) => !item.candidates.includes(region))
    : review.regions;

  return (
    <div className="stack">
      <PageHeader
        title="Açı Kontrolü"
        subtitle={`${remaining} fotoğraf bekliyor · ${review.settled + done} işaretlendi`}
      />

      {/*
        Said once, at the top, rather than beside every button. The model's
        answers are suggestions until a subject's benchmark clears the release
        gate, and no subject has.
      */}
      {review.auto_assignment_enabled ? (
        /*
          Automatic assignment is on, so the old sentence here - "the measured
          accuracy has not reached the level needed" - would be a lie. What
          this queue holds changed with it: not everything the model looked at,
          but the photographs it was *not* confident enough to write.
        */
        <Callout tone="neutral" title="Emin olunamayan fotoğraflar">
          Sistem yeterince emin olduğu fotoğrafları kendisi işaretledi. Burada
          kalanlar emin olamadıkları; hangisinin doğru olduğuna siz karar
          verirsiniz. Sizin seçiminiz kalıcıdır ve sonraki çalışmalar onu
          değiştirmez.
        </Callout>
      ) : (
        <Callout tone="neutral" title="Öneriler otomatik uygulanmaz">
          Sistem, her fotoğraf için olası açıları daraltır; hangisinin doğru
          olduğuna siz karar verirsiniz. Ölçülen doğruluk, otomatik işaretleme
          için gereken düzeye henüz ulaşmadı.
        </Callout>
      )}

      {!item ? (
        <Section title="Bekleyen fotoğraf yok">
          <p className="muted">
            {review.settled + done > 0
              ? "Bu projedeki fotoğrafların açıları işaretlendi."
              : "Bu projede henüz açı işaretlenmesi bekleyen fotoğraf yok."}
          </p>
        </Section>
      ) : (
        <Section title={item.media.original_filename}>
          <div className="region-review">
            <figure className="region-review__figure">
              <img
                src={apiUrl(`/media/${encodeURIComponent(item.media.id)}/preview`, {
                  size: "detail",
                })}
                alt={`${item.media.original_filename} önizlemesi`}
                decoding="async"
              />
              <figcaption className="faint">
                Küçültülmüş önizleme; yüklediğiniz dosyanın kendisi değildir.
              </figcaption>
            </figure>

            <div className="region-review__answers">
              {item.candidates.length > 0 ? (
                <div className="stack-tight">
                  <h3 className="region-review__heading">Öneriler</h3>
                  <div className="row">
                    {item.candidates.map((region) => (
                      <Button
                        key={region}
                        variant="primary"
                        disabled={busy}
                        onClick={() => void answer(region)}
                      >
                        {label(subject, region)}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="muted">
                  Bu fotoğraf için bir öneri üretilemedi. Aşağıdan siz seçin.
                </p>
              )}

              {/*
                Always present, even when there are two suggestions. A screen
                that hid the other ten stations would make the model's
                narrowing binding.

                The shortlisted ones are removed from this grid rather than
                repeated: two buttons with the same label on one screen is a
                reader wondering whether they do different things.
              */}
              <div className="stack-tight">
                <h3 className="region-review__heading">
                  {item.candidates.length > 0 ? "Diğer açılar" : "Tüm açılar"}
                </h3>
                <div className="region-review__grid">
                  {rest.map((region) => (
                    <Button
                      key={region}
                      disabled={busy}
                      onClick={() => void answer(region)}
                    >
                      {label(subject, region)}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="row">
                {/* A real decision, and not a skip: it settles the file so
                    nothing proposes an angle for it again. */}
                <Button disabled={busy} onClick={() => void answer(null)}>
                  Hiçbiri
                </Button>
                {/* Nothing is written. The photograph is here next time. */}
                <Button disabled={busy} onClick={advance}>
                  Sonraki
                </Button>
              </div>

              {failed && (
                <Callout tone="danger">
                  İşaretlenemedi. Fotoğraf değişmedi; tekrar deneyebilirsiniz.
                </Callout>
              )}
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}
